import http from '@utils/http';

const USERS_PAGE_LIMIT_MAX = 100;

/**
 * GET /api/v1/users
 * @param {{ page?: number, limit?: number }} params - limit is capped at 100
 */
export const getUsers = (params = {}) => {
    const safeParams = { ...params, limit: Math.min(Number(params.limit) || 10, USERS_PAGE_LIMIT_MAX) };
    return http.get('/users', { params: safeParams }).then((res) => {
        const payload = res.data ?? {};
        const list = payload.data;
        return {
            data: Array.isArray(list) ? list : [],
            total: payload.total ?? 0,
            page: payload.page ?? 1,
            limit: payload.limit ?? 10,
            totalPages: payload.totalPages ?? 1,
        };
    });
};

/**
 * GET /api/v1/users/:id
 */
export const getUserById = (id) => http.get(`/users/${id}`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/users/invite
 * @param {{ email: string, roleId: string }} body
 */
export const inviteUser = (body) => http.post('/users/invite', body).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/users/create
 * @param {{ email: string, name: string, roleId: string, password: string }} body
 */
export const createUser = (body) => http.post('/users/create', body).then((res) => res.data?.data ?? res.data);

const ALLOWED_USER_STATUSES = ['active', 'invited', 'suspended', 'deactivated'];

/**
 * Normalize status to an allowed API value (active, invited, suspended, deactivated).
 * Maps common variants (e.g. inactive -> deactivated).
 */
const normalizeUserStatus = (status) => {
    if (!status || typeof status !== 'string') return 'active';
    const s = status.trim().toLowerCase();
    if (ALLOWED_USER_STATUSES.includes(s)) return s;
    if (s === 'inactive') return 'deactivated';
    return 'active';
};

/**
 * PATCH /api/v1/users/:id – update role and/or status
 * @param {string} id – user id
 * @param {{ roleId?: string, status?: 'active'|'invited'|'suspended'|'deactivated' }} body
 */
export const updateUser = (id, body) => {
    const normalizedBody = { ...body };
    if (Object.prototype.hasOwnProperty.call(normalizedBody, 'status')) {
        normalizedBody.status = normalizeUserStatus(normalizedBody.status);
    }
    return http.patch(`/users/${id}`, normalizedBody).then((res) => res.data?.data ?? res.data);
};

/**
 * POST /api/v1/users/:id/removal-request – HR request removal (pending until Company Admin approves)
 * @param {string} id – user id
 */
export const requestRemoval = (id) =>
    http.post(`/users/${id}/removal-request`).then((res) => res.data?.data ?? res.data);

/**
 * GET /api/v1/users/removal-requests – list removal requests (optional status: pending|approved|rejected)
 * Response shape: { data: { items: [...], total, page, limit, totalPages } }
 * @param {{ page?: number, limit?: number, status?: string }} params
 */
export const getRemovalRequests = (params = {}) =>
    http.get('/users/removal-requests', { params }).then((res) => {
        const payload = res.data ?? {};
        const inner = payload.data ?? {};
        const list = inner.items ?? (Array.isArray(inner) ? inner : []);
        const data = Array.isArray(list) ? list : [];
        const total = inner.total ?? payload.total ?? data.length;
        const page = inner.page ?? payload.page ?? 1;
        const limit = inner.limit ?? payload.limit ?? 10;
        const totalPages = inner.totalPages ?? payload.totalPages ?? 1;
        return {
            data,
            total: typeof total === 'string' ? parseInt(total, 10) || 1 : total,
            page: typeof page === 'string' ? parseInt(page, 10) || 1 : page,
            limit: typeof limit === 'string' ? parseInt(limit, 10) || 10 : limit,
            totalPages: typeof totalPages === 'string' ? parseInt(totalPages, 10) || 1 : totalPages,
        };
    });

/**
 * POST /api/v1/users/removal-requests/:id/approve – Company Admin approve (executes removal)
 * @param {string} id – removal request id
 */
export const approveRemovalRequest = (id) =>
    http.post(`/users/removal-requests/${id}/approve`).then((res) => res.data?.data ?? res.data);

/**
 * POST /api/v1/users/removal-requests/:id/reject – Company Admin reject
 * @param {string} id – removal request id
 */
export const rejectRemovalRequest = (id) =>
    http.post(`/users/removal-requests/${id}/reject`).then((res) => res.data?.data ?? res.data);

/**
 * DELETE /api/v1/users/:id – Company Admin remove user from company (soft delete; requires saas.users.delete)
 * @param {string} id – user id
 */
export const deleteUser = (id) => http.delete(`/users/${id}`).then((res) => res.data?.data ?? res.data);
